import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, DollarSign } from "lucide-react";
import { ogImageMeta } from "@/lib/og-image";
import { trackApplyClick } from "@/lib/apply-cta";

const URL_SELF = "https://vendingmachinefree.com/zero-cost-workplace-vending";
const TITLE = "Zero-Cost Workplace Vending — $0 Machines, $0 Service | USA";
const DESC =
  "Zero-cost workplace vending machines and micro-markets for US businesses. $0 machine, $0 install, $0 restocking, $0 repairs. Full-service coverage nationwide.";

const FAQS = [
  {
    q: "How is workplace vending actually zero-cost?",
    a: "Our operator network is paid entirely by product sales. Your workplace supplies the space and power; the operator supplies the machine, product, service, and insurance. No line item hits your budget.",
  },
  {
    q: "Are there hidden fees, commission requirements, or minimums?",
    a: "None. No install fee, no monthly service fee, no product minimum, no long-term contract. If you'd rather receive a small commission on sales, ask during your site survey — most workplaces prefer the simpler zero-cost model.",
  },
  {
    q: "What workplaces qualify?",
    a: "Offices (20+ employees), warehouses, manufacturing, distribution centers, hospitals, gyms, schools, hotels, and shared multi-tenant buildings across all 50 states.",
  },
  {
    q: "How fast can zero-cost placement happen?",
    a: "Most workplaces are surveyed within 48 hours and fully placed within 1–2 weeks. No paperwork bottleneck — everything is at-will.",
  },
];

export const Route = createFileRoute("/zero-cost-workplace-vending")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Zero-Cost Workplace Vending",
          serviceType: "Zero-Cost Workplace Vending Placement",
          provider: { "@type": "Organization", name: "Vending Machine Free.com" },
          areaServed: { "@type": "Country", name: "United States" },
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: URL_SELF,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link to="/" hash="contact" onClick={() => trackApplyClick("zero_cost_header")} className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground">
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <DollarSign className="h-4 w-4" /> Zero-Cost Program
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Zero-Cost Workplace Vending
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            $0 machine · $0 install · $0 restocking · $0 repairs. Full-service vending, smart fridges, and micro-markets
            for US workplaces. No fees, no minimums, no contracts — operator revenue is entirely from product sales.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("zero_cost_hero")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">The Zero-Cost Model — Explained Simply</h2>
          <ol className="mt-6 space-y-3">
            {[
              "You supply the space (roughly 3×3 ft to 8×3 ft) and a standard 120V outlet.",
              "We supply the machine or micro-market at $0, delivered and installed.",
              "We stock it weekly with snacks, drinks, and (for micro-markets) fresh food.",
              "We clean, service, insure, and repair everything at $0 cost.",
              "We earn revenue purely from product sales — your business is never invoiced.",
            ].map((t, i) => (
              <li key={t} className="flex gap-3 items-start rounded-md border border-border bg-card p-4">
                <span className="h-7 w-7 rounded-md bg-safety text-safety-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-sm">{t}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-14 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">Where the $0 Model Fits</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {[
              "Corporate offices (20+ staff)",
              "Warehouses & distribution",
              "Manufacturing floors",
              "Hospitals & medical clinics",
              "Schools & universities",
              "Hotels & hospitality",
              "Gyms & fitness centers",
              "Shared multi-tenant buildings",
              "24/7 operations & call centers",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2 rounded-md bg-card border border-border p-3">
                <CheckCircle2 className="h-5 w-5 text-safety flex-shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl uppercase">Zero-Cost FAQs</h2>
          <div className="mt-8 divide-y divide-border border border-border rounded-xl bg-card">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="group p-6">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold list-none">
                  <span>{q}</span>
                  <span className="h-7 w-7 rounded-md bg-secondary flex items-center justify-center text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-charcoal text-white text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">Start Your Zero-Cost Placement</h2>
          <p className="mt-3 opacity-80">2-minute application. Local operator response within 24 hours.</p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("zero_cost_footer")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-8 text-sm">
            Related: <Link to="/free-office-vending-machine-placement" className="text-safety hover:underline">office placement</Link> ·{" "}
            <Link to="/free-micro-market-placement" className="text-safety hover:underline">micro-markets</Link> ·{" "}
            <Link to="/hubs" className="text-safety hover:underline">tech hubs</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
